// 261010 P2-1 (luồng Sky) — thẻ MỘT setup của "Sơ đồ định vị". Logic thuần ở positionMapUtils.ts.
import React, { useMemo, useState } from 'react';
import { Button, Collapse, Image, Input, Popconfirm, Select, Space, Spin, Tag, Tooltip } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { FilmCharacter, FilmCharacterLook, FilmLocation, PositionMap, PositionMapWriteBody } from '../../types/film';
import { actualCostLabel, authorBadge, canGenerate, castPayload, costRangeLabel, mapApprovalView, mapStatusTags, visibleZones, type CastChange } from './positionMapUtils';
import PromptChipEditor from './PromptChipEditor'; // 261010 P9D #29
import { mapAnchorOf, mapChipChoicesOf, mapEditorValueOf, mapOverrideIssueText, markChoicesNotReady } from './promptSurfaceUtils';
import { combineGate, confirmContent, genButtonLabel, spentLabel, type ActionGate } from './workflowActionPolicy'; // 261010 P2b-1

const LOOK_SOURCE_TEXT: Record<string, string> = { SCENE: 'theo cảnh', MAIN: 'bộ chính', USER: 'đã chọn' };
const REF_KIND_TEXT: Record<string, string> = { PLATE: 'bối cảnh', SHEET: 'sheet', ANCHOR: 'ảnh chính' };
const LONG_TEXT = 300;

export interface PositionMapCardProps {
  map: PositionMap;
  allMaps: PositionMap[];
  characters: FilmCharacter[];
  looks: FilmCharacterLook[];
  locations: FilmLocation[];
  /** Video/giai đoạn đang dùng setup này (đã dựng nhãn). */
  usage: string[];
  running: boolean;
  error?: string | null;
  /** Mọi ghi đi qua parent (parent báo lỗi + nạp lại GET). Trả `true` khi ghi thành công. */
  onPatch: (body: PositionMapWriteBody) => Promise<boolean>;
  onGenerate: () => void;
  onDelete: () => void;
  /** 261010 P4-1 — duyệt / bỏ duyệt (parent gọi API, báo lỗi, nạp lại GET). Chỉ người bấm; chỉ bản đã duyệt mới vào video. */
  onApprove: () => Promise<void>;
  onUnapprove: () => Promise<void>;
  /** 261010 P2b-1 (khung mới) — ẨN giá ở nhãn nút, dòng "đã tốn" và hộp xác nhận (thay bằng hộp không giá). Không truyền ⇒ UI cũ y nguyên. */
  hideCost?: boolean;
  /** 261010 P2b-1 — cổng từ `workflowActionPolicy` cho ba nút; chỉ THÊM ràng buộc (ẩn/tắt). Không truyền ⇒ y như cũ. */
  policy?: { generate?: ActionGate; approve?: ActionGate; unapprove?: ActionGate };
  /** 261010 P9D #29 (khung mới) — prompt sơ đồ là CHIP (loc/char/look, ảnh trong câu), lưu token vào `promptOverride`; ảnh setup chính hiện là chip chỉ đọc cuối. Không truyền ⇒ ô chữ thô như cũ (UI cũ 0-diff). */
  chipPrompt?: boolean;
  /** 261030 B2 — chỉ khi `hideCost`: dòng tiền thêm vào hộp xác nhận tạo ảnh sơ đồ. */
  genCostNote?: string;
}

/** Ô sửa chữ nhiều dòng, lưu bằng nút (không lưu khi blur — tránh ghi nhầm làm `author_status=EDITED`). */
const TextEditor: React.FC<{ initial: string; rows?: number; placeholder?: string; saveText?: string; onSave: (v: string) => Promise<boolean>; onCancel: () => void }> = ({ initial, rows = 4, placeholder, saveText = 'Lưu', onSave, onCancel }) => {
  const [v, setV] = useState(initial);
  const [saving, setSaving] = useState(false);
  return (
    <div>
      <Input.TextArea autoFocus value={v} placeholder={placeholder} autoSize={{ minRows: rows, maxRows: 14 }} onChange={(e) => setV(e.target.value)} />
      <Space style={{ marginTop: 4 }}>
        <Button size="small" type="primary" loading={saving} disabled={!v.trim()} onClick={async () => { setSaving(true); const ok = await onSave(v.trim()); setSaving(false); if (ok) onCancel(); }}>{saveText}</Button>
        <Button size="small" onClick={onCancel}>Huỷ</Button>
      </Space>
    </div>
  );
};

const PositionMapCard: React.FC<PositionMapCardProps> = ({ map, allMaps, characters, looks, locations, usage, running, error, onPatch, onGenerate, onDelete, onApprove, onUnapprove, hideCost, policy, chipPrompt, genCostNote }) => {
  const [approving, setApproving] = useState<null | 'approve' | 'unapprove'>(null);
  const [editingText, setEditingText] = useState(false);
  const [editingPrompt, setEditingPrompt] = useState(false);
  const [moreText, setMoreText] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameDraft, setNameDraft] = useState(map.name);
  const [castBusy, setCastBusy] = useState(false);

  const location = locations.find((l) => l.id === map.locationId) || null;
  const zones = visibleZones(location?.zones);
  const anchor = map.anchorMapId ? allMaps.find((m) => m.id === map.anchorMapId) : null;
  const tags = mapStatusTags(map, { running, error });
  const badge = authorBadge(map.authorStatus);
  const baseGate = canGenerate(map, allMaps, { running });
  const issuesNow = chipPrompt ? (map.overrideIssues ?? []) : [];
  const ownGate = baseGate.ok && issuesNow.length ? { ok: false, reason: `Prompt có chip không dùng được: ${issuesNow.map(mapOverrideIssueText)[0]}` } : baseGate;
  const genG = combineGate(ownGate, policy?.generate);
  const gate = { ok: genG.ok, reason: genG.reason };
  const approval = mapApprovalView(map);
  const runApproval = async (kind: 'approve' | 'unapprove') => {
    setApproving(kind);
    try { await (kind === 'approve' ? onApprove() : onUnapprove()); } finally { setApproving(null); }
  };
  const hasImg = !!map.imageUrl;
  const cost = costRangeLabel(map.role, map.estimatedCostUsd);
  const spent = spentLabel(actualCostLabel(map.imageCostUsd), hideCost);
  const charName = (id: string) => characters.find((c) => c.id === id)?.name || `(nhân vật ${id.slice(0, 6)})`;
  const looksOf = (characterId: string) => looks.filter((l) => l.characterId === characterId);
  const anchors = allMaps.filter((m) => m.role === 'ANCHOR' && m.locationId === map.locationId && m.id !== map.id);
  const long = map.blockingText.length > LONG_TEXT;
  // 261010 P9D #29 — chip: bản đang sửa tay (có token) hiện nguyên; còn lại token hoá prompt dựng sẵn từ refsPreview + cast.
  const overrideIssues = chipPrompt ? (map.overrideIssues ?? []) : [];
  const chipChoices = useMemo(() => (chipPrompt ? markChoicesNotReady(mapChipChoicesOf({
    locationId: map.locationId ?? null, locationName: location?.name || 'Bối cảnh', plateUrl: map.refsPreview.find((r) => r.kind === 'PLATE')?.url ?? null,
    characters, looks,
  }), map.overrideIssues) : []), [chipPrompt, map.locationId, map.refsPreview, map.overrideIssues, location, characters, looks]); // eslint-disable-line react-hooks/exhaustive-deps
  const chipValue = useMemo(() => {
    if (!chipPrompt) return '';
    return mapEditorValueOf({
      promptOverride: map.promptOverride, renderedPrompt: map.renderedPrompt,
      locationId: map.locationId ?? null, locationName: location?.name || 'Bối cảnh',
      cast: map.castJson.map((c) => ({ characterId: c.characterId, lookId: c.lookId, name: charName(c.characterId) })), refsPreview: map.refsPreview,
    });
  }, [chipPrompt, map.promptOverride, map.renderedPrompt, map.refsPreview, map.castJson, map.locationId, location]); // eslint-disable-line react-hooks/exhaustive-deps
  const anchorRef = chipPrompt ? mapAnchorOf(map.refsPreview) : null;
  const border = running ? '#91caff' : error ? '#ff7875' : (map.staleness === 'INPUT_CHANGED' || map.staleness === 'ANCHOR_REGENERATED') ? '#ffc069' : '#d9d9d9';

  const patchCast = async (change: CastChange) => {
    setCastBusy(true);
    try { await onPatch({ castJson: castPayload(map.castJson, change) }); } finally { setCastBusy(false); }
  };

  const genConfirm = hideCost ? confirmContent('GEN_IMAGE', 'MAP') : null;
  const genButton = (
    <Popconfirm
      title={genConfirm ? genConfirm.title : `${hasImg ? 'Gen lại' : 'Gen'} ảnh "${map.name}"?`}
      description={genConfirm
        ? <div style={{ maxWidth: 300 }}>{genConfirm.body}{genCostNote && <div data-testid="map-gen-cost" style={{ marginTop: 4 }}><b>{genCostNote}</b></div>}</div>
        : <div style={{ maxWidth: 280 }}>Giá ước <b>{cost}</b> (dao động theo số ảnh tham chiếu){hasImg ? '. Ảnh đang dùng sẽ bị thay.' : '.'}</div>}
      okText={genConfirm ? genConfirm.okText : (hasImg ? 'Gen lại' : 'Gen')} cancelText={genConfirm ? genConfirm.cancelText : 'Thôi'} disabled={!gate.ok} onConfirm={onGenerate}
    >
      <Button size="small" type="primary" loading={running} disabled={!gate.ok}>{genButtonLabel({ hasImage: hasImg, noun: 'ảnh', cost, hideCost })}</Button>
    </Popconfirm>
  );

  return (
    <div data-testid={`pm-card-${map.name}`} style={{ border: `1px solid ${border}`, borderRadius: 8, background: '#fff', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <div style={{ padding: '8px 12px', display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap', borderBottom: '1px solid #f0f0f0', background: '#fafafa' }}>
        {editingName ? (
          <Input size="small" autoFocus style={{ width: 200 }} value={nameDraft} onChange={(e) => setNameDraft(e.target.value)}
            onBlur={async () => { setEditingName(false); const n = nameDraft.trim(); if (n && n !== map.name) await onPatch({ name: n }); else setNameDraft(map.name); }}
            onPressEnter={(e) => (e.target as HTMLInputElement).blur()} onKeyDown={(e) => { if (e.key === 'Escape') { setNameDraft(map.name); setEditingName(false); } }} />
        ) : (
          <Tooltip title="Bấm để đổi tên"><b style={{ cursor: 'pointer' }} onClick={() => { setNameDraft(map.name); setEditingName(true); }}>{map.name}</b></Tooltip>
        )}
        <Tag color={map.role === 'ANCHOR' ? 'blue' : 'default'} style={{ margin: 0 }}>{map.role === 'ANCHOR' ? 'chính' : 'phụ'}</Tag>
        {badge && <Tooltip title={badge.tip}><Tag color={map.authorStatus === 'AGENT' ? 'purple' : undefined} style={{ margin: 0 }} data-testid={`pm-author-${map.name}`}>{badge.text}</Tag></Tooltip>}
        {tags.map((t) => (
          <Tooltip key={t.key} title={t.tip}><Tag color={t.color} style={{ margin: 0 }}>{t.key === 'running' && <Spin size="small" style={{ marginRight: 4 }} />}{t.text}</Tag></Tooltip>
        ))}
        <Tooltip title={approval.tag.tip}><Tag color={approval.tag.color} style={{ margin: 0 }} data-testid={`pm-approval-${map.name}`}>{approval.tag.text}</Tag></Tooltip>
        <span style={{ flex: 1 }} />
        <Popconfirm title={`Xoá setup "${map.name}"?`} description="Liên kết của nó cũng bị xoá. Ảnh trên R2 giữ lại." okText="Xoá" okButtonProps={{ danger: true }} cancelText="Thôi" onConfirm={onDelete}>
          <Button size="small" type="text" danger icon={<DeleteOutlined />} aria-label="Xoá setup" />
        </Popconfirm>
      </div>

      <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
        <div style={{ width: '100%', aspectRatio: '16 / 9', background: '#0b0f19', borderRadius: 6, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', position: 'relative' }}>
          {hasImg
            ? <Image src={map.imageUrl!} alt={map.name} width="100%" height="100%" style={{ objectFit: 'contain', opacity: map.staleness === 'FRESH' ? 1 : 0.75 }} />
            : <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: 16 }}>Chưa có ảnh sơ đồ{!gate.ok && gate.reason ? <div style={{ fontSize: 11, marginTop: 4 }}>{gate.reason}</div> : null}</div>}
          {running && <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', gap: 8 }}><Spin /> Đang gen ảnh…</div>}
        </div>

        <div style={{ fontSize: 12, color: '#666', display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <span>Bối cảnh: <b>{location?.name || '—'}</b></span>
          {zones.length > 0 && (
            <span>Zone:{' '}
              <Select size="small" allowClear placeholder="không" style={{ minWidth: 150 }} value={map.zoneId || undefined}
                options={zones.map((z) => ({ value: z.id, label: z.name }))} onChange={(v) => { onPatch({ zoneId: v || null }); }} />
            </span>
          )}
          {map.role === 'DERIVATIVE' && (
            <span>Neo:{' '}
              <Select size="small" style={{ minWidth: 170 }} value={map.anchorMapId || undefined} placeholder="chọn ảnh chính"
                options={anchors.concat(anchor && !anchors.includes(anchor) ? [anchor] : []).map((m) => ({ value: m.id, label: m.name }))}
                onChange={(v) => { onPatch({ anchorMapId: v }); }} />
            </span>
          )}
          {hasImg && <span style={{ color: '#888' }}>{[map.imageSize, spent].filter(Boolean).join(' · ')}</span>}
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2 }}>Nhân vật & look {castBusy && <Spin size="small" />}</div>
          {map.castJson.length === 0 && <div style={{ fontSize: 12, color: '#999' }}>Chưa có nhân vật nào.</div>}
          {map.castJson.map((c) => (
            <div key={c.characterId} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 12, marginBottom: 3 }}>
              <span style={{ minWidth: 110, fontWeight: 500 }}>{charName(c.characterId)}</span>
              <Select size="small" style={{ minWidth: 140, flex: 1 }} value={c.lookId || undefined} placeholder="tự động" allowClear disabled={castBusy}
                options={looksOf(c.characterId).map((l) => ({ value: l.id, label: l.lookKey }))}
                onChange={(v) => patchCast({ type: 'look', characterId: c.characterId, lookId: v || null })} />
              <Tooltip title={c.lookSource === 'USER' ? 'Look do người chọn — không tự đổi theo cảnh.' : c.lookSource === 'SCENE' ? 'Look nhân vật mặc ở cảnh đầu tiên setup này dùng.' : 'Bộ chính của nhân vật (chưa có look theo cảnh).'}>
                <Tag style={{ margin: 0, fontSize: 10 }}>{LOOK_SOURCE_TEXT[c.lookSource] || c.lookSource}</Tag>
              </Tooltip>
              <Button size="small" type="text" disabled={castBusy} aria-label={`Bỏ ${charName(c.characterId)}`} onClick={() => patchCast({ type: 'remove', characterId: c.characterId })}>✕</Button>
            </div>
          ))}
          <Select size="small" style={{ minWidth: 190 }} placeholder="+ thêm nhân vật" value={undefined} disabled={castBusy} showSearch optionFilterProp="label"
            options={characters.filter((c) => !map.castJson.some((x) => x.characterId === c.id)).map((c) => ({ value: c.id, label: c.name }))}
            onChange={(v) => { if (v) patchCast({ type: 'add', characterId: v }); }} />
        </div>

        <div>
          <div style={{ fontSize: 12, fontWeight: 600, marginBottom: 2, display: 'flex', alignItems: 'center' }}>
            Chữ dàn cảnh<span style={{ flex: 1 }} />
            {!editingText && <Button size="small" type="link" onClick={() => setEditingText(true)}>Sửa</Button>}
          </div>
          {editingText
            ? <TextEditor initial={map.blockingText} rows={5} onSave={(v) => onPatch({ blockingText: v })} onCancel={() => setEditingText(false)} />
            : (
              <>
                <div data-testid="pm-blocking" style={{ fontSize: 12, background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 4, padding: '6px 8px', whiteSpace: 'pre-wrap', ...(long && !moreText ? { maxHeight: 76, overflow: 'hidden' } : {}) }}>{map.blockingText}</div>
                {long && <Button size="small" type="link" style={{ padding: 0 }} onClick={() => setMoreText((v) => !v)}>{moreText ? 'Thu gọn' : 'Xem hết'}</Button>}
              </>
            )}
        </div>

        <Collapse size="small" ghost items={[
          {
            key: 'prompt',
            label: <span style={{ fontSize: 12 }}>Prompt đã render{map.promptOverride ? ' · sửa tay' : ''}</span>,
            children: chipPrompt ? (
              <div data-testid="pm-prompt-chip">
                {overrideIssues.length ? (
                  <ul className="wf-alert ERROR" role="alert" data-testid="pm-override-issues" style={{ margin: '0 0 6px', paddingLeft: 22, fontSize: 12 }}>
                    {overrideIssues.map((c) => <li key={c}>{mapOverrideIssueText(c)}</li>)}
                  </ul>
                ) : null}
                {map.renderedPrompt || map.promptOverride
                  ? <PromptChipEditor value={chipValue} extraChoices={chipChoices} emptyText="(chưa dựng được prompt)" onSave={(v) => { void onPatch({ promptOverride: v }); }} />
                  : <span style={{ fontSize: 12, color: '#999' }}>(chưa dựng được prompt — thiếu ảnh bối cảnh/plate?)</span>}
                {anchorRef ? (
                  <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }} data-testid="pm-anchor-chip">
                    <span style={{ color: '#666' }}>Ảnh setup chính (tự đính cuối, không gỡ):</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: '1px solid #1677ff', borderRadius: 11, padding: '1px 6px 1px 1px', background: '#1677ff14', color: '#1677ff' }}>
                      <Image src={anchorRef.url} width={18} height={18} style={{ objectFit: 'cover', borderRadius: 3 }} preview={false} />
                      <span>{anchor?.name || 'ảnh chính'}</span>
                    </span>
                  </div>
                ) : null}
                {map.promptOverride != null && map.promptOverride !== '' && (
                  <div style={{ marginTop: 4 }}>
                    <Popconfirm title="Về prompt mặc định (theo mẫu)?" description="Bản sửa tay sẽ bị bỏ." okText="Về mặc định" cancelText="Thôi" onConfirm={() => { onPatch({ promptOverride: null }); }}>
                      <Button size="small">Về mặc định</Button>
                    </Popconfirm>
                  </div>
                )}
              </div>
            ) : editingPrompt
              ? <TextEditor initial={map.renderedPrompt} rows={8} saveText="Lưu prompt sửa tay" onSave={(v) => onPatch({ promptOverride: v })} onCancel={() => setEditingPrompt(false)} />
              : (
                <div>
                  <pre data-testid="pm-prompt" style={{ fontSize: 11, whiteSpace: 'pre-wrap', margin: 0, maxHeight: 220, overflow: 'auto', background: '#fafafa', padding: 6, borderRadius: 4 }}>{map.renderedPrompt || '(chưa dựng được prompt — thiếu ảnh bối cảnh/plate?)'}</pre>
                  <Space style={{ marginTop: 4 }}>
                    <Button size="small" onClick={() => setEditingPrompt(true)} disabled={!map.renderedPrompt}>Sửa prompt</Button>
                    {map.promptOverride != null && map.promptOverride !== '' && (
                      <Popconfirm title="Về prompt mặc định (theo mẫu)?" description="Bản sửa tay sẽ bị bỏ." okText="Về mặc định" cancelText="Thôi" onConfirm={() => { onPatch({ promptOverride: null }); }}>
                        <Button size="small">Về mặc định</Button>
                      </Popconfirm>
                    )}
                  </Space>
                </div>
              ),
          },
          ...(chipPrompt ? [] : [{
            key: 'refs',
            label: <span style={{ fontSize: 12 }}>Ảnh tham chiếu ({map.refsPreview.length})</span>,
            children: map.refsPreview.length === 0 ? <span style={{ fontSize: 12, color: '#999' }}>Chưa dựng được danh sách ảnh tham chiếu.</span> : (
              <div data-testid="pm-refs">
                {map.refsPreview.map((r) => (
                  <div key={r.slot} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 11, marginBottom: 4 }}>
                    <b style={{ minWidth: 18 }}>{r.slot}</b>
                    <Tag style={{ margin: 0 }} color={r.kind === 'ANCHOR' ? 'blue' : r.kind === 'PLATE' ? 'green' : 'default'}>{REF_KIND_TEXT[r.kind] || r.kind}</Tag>
                    <Image src={r.url} width={36} height={36} style={{ objectFit: 'cover', borderRadius: 3 }} />
                    <span style={{ flex: 1, color: '#555' }}>{r.label}</span>
                  </div>
                ))}
              </div>
            ),
          }]),
        ]} />
      </div>

      <div style={{ padding: '8px 12px', borderTop: '1px solid #f0f0f0', background: '#fafafa', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, color: '#666', flex: 1 }} data-testid="pm-usage">
          {usage.length ? <>Dùng cho: {usage.map((u) => <Tag key={u} style={{ margin: '0 4px 0 0', fontSize: 10 }}>{u}</Tag>)}</> : 'Chưa gán cho video nào'}
        </span>
        {approval.canUnapprove && (policy?.unapprove?.visible ?? true) && (
          <Tooltip title="Gỡ duyệt: sơ đồ này thôi được đính vào video."><Button size="small" loading={approving === 'unapprove'} disabled={running || approving !== null || policy?.unapprove?.enabled === false} onClick={() => runApproval('unapprove')} data-testid={`pm-unapprove-${map.name}`}>Bỏ duyệt</Button></Tooltip>
        )}
        {approval.approve && (policy?.approve?.visible ?? true) && (
          <Tooltip title={approval.approve.ok ? 'Chỉ bản đã duyệt mới vào video.' : approval.approve.reason}>
            <span><Button size="small" type={approval.approve.ok ? 'primary' : 'default'} ghost={approval.approve.ok} loading={approving === 'approve'} disabled={!approval.approve.ok || running || approving !== null || policy?.approve?.enabled === false} onClick={() => runApproval('approve')} data-testid={`pm-approve-${map.name}`}>{approval.approve.label}</Button></span>
          </Tooltip>
        )}
        {genG.visible && (gate.ok ? genButton : <Tooltip title={gate.reason}><span>{genButton}</span></Tooltip>)}
      </div>
    </div>
  );
};

export default PositionMapCard;
